import React from 'react';

// Tabla de datos genérica. Reutiliza el CSS de tabla global del proyecto
// (table/th/td) más la clase .data-table (zebra, hover, densidad, utilidades).
//
// Props:
//   columns    : array de { key, header, render?, className?, fixed? }
//                - key      : identificador de columna (y campo de row si no hay render)
//                - header   : contenido del <th>
//                - render   : (row) => ReactNode  (opcional; por defecto row[key])
//                - className: clase para <th> y <td> ('num', 'dt-nowrap', 'dt-strong',
//                             'col-actions'…)
//   rows       : array de filas
//   rowKey     : (row) => key React  (por defecto row.id)
//   getRowProps: (row) => props extra para el <tr> (style/className por estado);
//                con segundaFila van al <tbody> del registro, que abarca sus dos filas
//   hiddenKeys : array de keys de columna a NO mostrar. Qué se puede ocultar,
//                el orden y su persistencia los gobierna la pantalla (hook
//                useFieldLayout + VistaControles): la pantalla pasa las columnas
//                ya reordenadas (fl.orderColumns) y las ocultas en hiddenKeys.
//                El orden y la visibilidad son PROPIOS de cada modalidad (la
//                tabla y las tarjetas se configuran por separado).
//   segundaFila: keys de columna que, en vez de ocupar su columna, se pintan en
//                una segunda fila bajo los datos de cada registro, seguidas en
//                horizontal y en el orden de las columnas. Cada registro va en
//                su propio <tbody>, de modo que la cebra, el resalte al pasar el
//                ratón y la raya de separación son por registro. Si todas sus
//                celdas vienen vacías (null), la segunda fila no se pinta: así
//                puede servir para lo que se abre a demanda (Conversaciones).
//
// La columna de acciones (key 'actions') sale siempre la primera, a la
// izquierda, en todas las tablas, la declare la pantalla donde la declare; si
// va en la segunda fila, es lo primero de esa fila.
const DataTable = ({ columns, rows, rowKey = (r) => r.id, getRowProps, className = '', hiddenKeys = [], segundaFila = [] }) => {
  const hidden = new Set(hiddenKeys);
  const shownColumns = columns.filter((col) => !hidden.has(col.key));
  const visibleColumns = [
    ...shownColumns.filter((col) => col.key === 'actions'),
    ...shownColumns.filter((col) => col.key !== 'actions'),
  ];
  const abajo = new Set(segundaFila);
  const columnasArriba = visibleColumns.filter((col) => !abajo.has(col.key));
  const columnasAbajo = visibleColumns.filter((col) => abajo.has(col.key));
  const celda = (col, row) => (col.render ? col.render(row) : row[col.key]);

  if (columnasAbajo.length > 0) {
    return (
      <div className="table-container">
        <table className={`data-table dt-dos-filas ${className}`.trim()}>
          <thead>
            <tr>
              {columnasArriba.map((col) => (
                <th key={col.key} className={col.className || undefined}>{col.header}</th>
              ))}
            </tr>
          </thead>
          {rows.map((row, i) => {
            const { className: extraClass, ...extra } = getRowProps ? getRowProps(row) : {};
            const clases = [i % 2 === 1 ? 'dt-par' : '', extraClass || ''].join(' ').trim();
            const celdasAbajo = columnasAbajo.map((col) => ({ key: col.key, contenido: celda(col, row) }));
            const hayAbajo = celdasAbajo.some((c) => c.contenido !== null && c.contenido !== undefined && c.contenido !== false && c.contenido !== '');
            return (
              <tbody key={rowKey(row)} className={clases || undefined} {...extra}>
                <tr className="dt-fila-datos">
                  {columnasArriba.map((col) => (
                    <td key={col.key} className={col.className || undefined}>{celda(col, row)}</td>
                  ))}
                </tr>
                {hayAbajo && (
                  <tr className="dt-segunda-fila">
                    <td colSpan={columnasArriba.length}>
                      <div className="dt-segunda-fila-contenido">
                        {celdasAbajo.map((c) => (
                          <React.Fragment key={c.key}>{c.contenido}</React.Fragment>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            );
          })}
        </table>
      </div>
    );
  }

  return (
    <div className="table-container">
      <table className={`data-table ${className}`.trim()}>
        <thead>
          <tr>
            {visibleColumns.map((col) => (
              <th key={col.key} className={col.className || undefined}>{col.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const extra = getRowProps ? getRowProps(row) : {};
            return (
              <tr key={rowKey(row)} {...extra}>
                {visibleColumns.map((col) => (
                  <td key={col.key} className={col.className || undefined}>
                    {celda(col, row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DataTable;
